/**
 * Test Fixture Runner — Admin Diagnostics card (Feb 2026)
 * =========================================================
 *
 * A standing, super-admin-only test project fixture. Use it to smoke-test
 * any feature (RFP send, takeoff, estimating, lifecycle, etc.) without
 * polluting real client data.
 *
 * Idempotent: re-clicking BUILD only inserts missing children. RESET wipes
 * children and reseeds canonical state. REMOVE fully deletes.
 *
 * Endpoints:
 *   GET    /api/admin/test-fixtures           — status
 *   POST   /api/admin/test-fixtures/build     — idempotent build/repair
 *   POST   /api/admin/test-fixtures/reset     — wipe children + reseed
 *   DELETE /api/admin/test-fixtures           — full removal
 *
 * Visibility model: the fixture is filtered OUT of every normal surface
 * for every user (super-admin included). It only surfaces here, on the
 * super-admin My Desk "Test Fixtures" section, and inside its own detail
 * page (which renders an amber banner so nothing is confused with a
 * real project).
 *
 * data-testids:
 *   test-fixture-runner-card
 *   test-fixture-runner-toggle
 *   test-fixture-runner-build-btn
 *   test-fixture-runner-reset-btn
 *   test-fixture-runner-remove-btn
 *   test-fixture-runner-status
 *   test-fixture-runner-open-link
 */

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FlaskConical, ChevronDown, ChevronRight, Loader2,
  CheckCircle2, RefreshCw, Trash2, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function TestFixtureRunnerCard() {
  const [hidden, setHidden] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Super-admin gate (UI-side). The backend still 403s non-super-admins.
  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((r) => setHidden(!r.data?.is_super_admin))
      .catch(() => setHidden(true));
  }, []);

  const fetchStatus = useCallback(async () => {
    if (hidden) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/test-fixtures`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      console.warn('Fixture status fetch failed:', e?.response?.status);
      setStatus({ exists: false });
    } finally {
      setLoading(false);
    }
  }, [hidden]);

  useEffect(() => {
    if (!hidden && expanded && status === null) {
      fetchStatus();
    }
  }, [hidden, expanded, status, fetchStatus]);

  if (hidden) return null;

  const runAction = async (path, method, label, confirmCopy) => {
    if (confirmCopy && !window.confirm(confirmCopy)) return;
    setBusy(true);
    try {
      const url = `${API}/api/admin/test-fixtures${path}`;
      const opts = { headers: authHeaders() };
      const r = method === 'delete'
        ? await axios.delete(url, opts)
        : await axios.post(url, {}, opts);
      toast.success(`Fixture ${label}`);
      // Build/Reset return { result, status }; Remove returns { result }.
      const newStatus = r.data?.status ?? (await axios.get(`${API}/api/admin/test-fixtures`, opts)).data;
      setStatus(newStatus);
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onBuild = () => runAction('/build', 'post', 'built / repaired');
  const onReset = () => runAction('/reset', 'post', 'reset', 'Reset the test fixture? This wipes its rooms, takeoff, estimate, RFPs, and the test contact, then restores them to the canonical baseline. The project itself stays.');
  const onRemove = () => runAction('', 'delete', 'removed', 'Fully delete the test fixture project, contacts, and all children? You can rebuild it any time.');

  const exists = !!status?.exists;
  const projectId = status?.project_id;
  const counts = status?.counts || {};

  return (
    <Card data-testid="test-fixture-runner-card" className="border-amber-200">
      <CardHeader>
        <button
          data-testid="test-fixture-runner-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-start gap-3 text-left"
        >
          {expanded ? <ChevronDown className="w-5 h-5 mt-0.5 text-amber-700" /> : <ChevronRight className="w-5 h-5 mt-0.5 text-amber-700" />}
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-amber-600" />
              Test Fixture (super-admin)
              {exists && (
                <Badge variant="outline" className="ml-2 border-amber-300 text-amber-800 bg-amber-50">
                  Active
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Standing sandbox project for smoke-testing features. Invisible to normal users, RFP sends auto-route to <code>delivered@resend.dev</code>.
            </CardDescription>
          </div>
        </button>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* What's inside */}
          <div className="rounded-md bg-amber-50/60 border border-amber-200 p-3 text-sm text-amber-900">
            <div className="font-semibold mb-1">What the fixture contains</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Project tagged <code>🧪 TEST FIXTURE</code> with 3 phases (current = Schematic Design)</li>
              <li>3 rooms embedded in <code>project.rooms[]</code> (Kitchen, Master Bath, Living Room)</li>
              <li>1 takeoff doc with 3 room entries + 1 estimate doc with 4 line items</li>
              <li>1 test contractor contact with email <code>delivered@resend.dev</code> (Resend sink)</li>
              <li>1 draft RFP wired to that contractor — ready to hit Send</li>
              <li>Visible on the super-admin&apos;s Projects list with amber dashed border; invisible to non-super-admins</li>
            </ul>
          </div>

          {/* Status panel */}
          <div data-testid="test-fixture-runner-status" className="rounded-md border bg-muted/30 p-3 text-sm">
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading status…
              </div>
            ) : exists ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">Fixture exists</span>
                  <code className="text-xs text-muted-foreground">{projectId}</code>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  <Stat label="Rooms" value={counts.rooms ?? 0} />
                  <Stat label="Takeoff" value={counts.takeoff_items ?? 0} />
                  <Stat label="Estimate" value={counts.estimate_items ?? 0} />
                  <Stat label="RFPs" value={counts.rfps ?? 0} />
                  <Stat label="Contacts" value={counts.contacts ?? 0} />
                </div>
                <div className="pt-1">
                  <a
                    data-testid="test-fixture-runner-open-link"
                    href={`/projects/${projectId}/hub`}
                    className="inline-flex items-center gap-1 text-amber-700 hover:underline text-xs font-medium"
                  >
                    Open fixture project <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">No fixture present in this tenant. Click <strong>Build / Repair</strong> below.</div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="test-fixture-runner-build-btn"
              onClick={onBuild}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
              {exists ? 'Repair Fixture' : 'Build Fixture'}
            </Button>
            <Button
              data-testid="test-fixture-runner-reset-btn"
              onClick={onReset}
              disabled={busy || !exists}
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Reset to Clean Baseline
            </Button>
            <Button
              data-testid="test-fixture-runner-remove-btn"
              onClick={onRemove}
              disabled={busy || !exists}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Fixture
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded border bg-white px-2 py-1.5">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-semibold text-amber-900">{value}</div>
    </div>
  );
}
